import { FormControlLabel } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { CheckboxOne } from '../../../../components/CheckboxOne/CheckboxOne.component';
import { ModalWithButtons } from '../../../../components/ModalWithButtons/ModalWithButtons.component';
import { Prompt } from '../../../../components/Prompt/Prompt.component';
import { ExternalSystem } from '../../../../components/SystemSettings/ConfigurationApi/configurationApi.types';
import { useLayoutContext } from '../../../../Context/Layout.context';
import useSessionUser from '../../../../hooks/useSessionUser';
import { getCommitteeIds, getCommitteeLabel } from '../../../../models/committees.types';
import { userOkeId } from '../../../../services/users/identity/user.service';
import { importExaminersFromExternalSystem } from '../../../../services/users/users.service';
import { ImportExaminersFromFileProps } from './ImportExaminersFromFile.components';
import { useTranslation } from 'react-i18next';

interface CheckboxItem {
  id: number;
  label: string;
  checked: boolean;
}
const committeeIds = getCommitteeIds();

interface ImportExaminersFromExternalSystemProps extends ImportExaminersFromFileProps {
  system: ExternalSystem;
}

export const ImportExaminersFromExternalSystem: React.FC<ImportExaminersFromExternalSystemProps> = (props) => {
  const { user } = useSessionUser();
  const committeId = userOkeId(user);
  const { openAlert, showLoader } = useLayoutContext();
  const { t } = useTranslation();
  const [checkboxes, setCheckboxes] = useState<CheckboxItem[]>(
    committeeIds.map((id) => {
      return { id: id, label: getCommitteeLabel(id), checked: false };
    }),
  );

  const handleChange = (id: number) => {
    setCheckboxes((prevCheckboxes) =>
      prevCheckboxes.map((checkbox) => (checkbox.id === id ? { ...checkbox, checked: !checkbox.checked } : checkbox)),
    );
  };

  const renderCheckbox = (checkbox: CheckboxItem) => (
    <Grid item xs={6} key={checkbox.id} style={{ paddingLeft: '75px' }}>
      <FormControlLabel
        control={<CheckboxOne checked={checkbox.checked} onChange={() => handleChange(checkbox.id)} />}
        label={checkbox.label}
      />
    </Grid>
  );

  const areAllUnchecked = checkboxes.every((checkbox) => !checkbox.checked);

  const handleConfirm = useCallback(() => {
    showLoader(true);
    importExaminersFromExternalSystem(
      props.system,
      checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.id),
    )
      .then(() => {
        openAlert('success', `Przyjęto zlecenie importu egzaminatorów z ${props.system.toUpperCase()}`);
        props.onClose();
      })
      .catch(() =>
        openAlert('error', `Zlecenie importu egzaminatorów z ${props.system.toUpperCase()} nie powiodło się`),
      )
      .finally(() => showLoader(false));
  }, [checkboxes]);

  if (props.permissions.hasAdminExaminerCreate) {
    return (
      <ModalWithButtons
        open
        title={`Import z ${props.system.toUpperCase()}`}
        xCloseButton={props.onClose}
        onCancel={props.onClose}
        onConfirm={handleConfirm}
        confirmButtonDisabled={areAllUnchecked}
        confirmButtonLabel={'ZATWIERDŹ'}
        autoHeight
      >
        <Grid container justifyContent="center" spacing={2} style={{ paddingBottom: '50px' }}>
          <Grid item>
            <Typography variant="h6">{`Wybierz ${t('global:shortNames.OKE')}, z jakich chcesz zaimportować egzaminatorów`}</Typography>
          </Grid>
          <Grid item xs={8}>
            <Grid container spacing={2}>
              {checkboxes.map(renderCheckbox)}
            </Grid>
          </Grid>
        </Grid>
      </ModalWithButtons>
    );
  } else if (props.permissions.hasAdminOkeExaminerCreate)
    return (
      <Prompt
        type="confirmation"
        mainActionButtonText="TAK"
        open
        mainText={`Czy na pewno chcesz zaimportować egzaminatorów z ${getCommitteeLabel(
          committeId,
        )} poprzez ${props.system.toUpperCase()}?`}
        onConfirm={handleConfirm}
        onCancel={props.onClose}
      />
    );
  else return <></>;
};
